import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, Download, Search, CheckCircle, ExternalLink, FileBarChart, Sparkles, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { ExecutiveValueReport } from '../components/ExecutiveValueReport';
import { openReportBuilderPhoebe } from '../components/GlobalPhoebeChat';
export function ReportsPage() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedResult, setSelectedResult] = useState<{
    id: number;
    name: string;
    type: string;
    initials: string;
  } | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  // Quick options shown on focus, grouped by section
  const quickOptionSections = [{
    label: 'Reports',
    items: [{
      id: 100,
      name: 'All Skills Report',
      type: 'Report',
      initials: 'AS'
    }, {
      id: 101,
      name: 'All Learners Report',
      type: 'Report',
      initials: 'AL'
    }, {
      id: 102,
      name: 'Reflections Survey Report',
      type: 'Report',
      initials: 'RS'
    }, {
      id: 103,
      name: 'Assessment Report',
      type: 'Report',
      initials: 'AR'
    }]
  }, {
    label: 'Learners',
    items: [{
      id: 200,
      name: 'John Fisher',
      type: 'Learner',
      initials: 'JF'
    }, {
      id: 201,
      name: 'David Taylor',
      type: 'Learner',
      initials: 'DT'
    }, {
      id: 202,
      name: 'Tom Zoldos',
      type: 'Learner',
      initials: 'TZ'
    }]
  }, {
    label: 'Skills',
    items: [{
      id: 300,
      name: 'HTML Basics',
      type: 'Skill',
      initials: 'HB'
    }, {
      id: 301,
      name: 'CSS Fundamentals',
      type: 'Skill',
      initials: 'CF'
    }, {
      id: 302,
      name: 'JavaScript Basics',
      type: 'Skill',
      initials: 'JB'
    }]
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
    };
    if (isUserMenuOpen || isSearchFocused) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isSearchFocused]);
  // Searchable items (users, skills, reports)
  const searchableItems = [{
    id: 1,
    name: 'Linda Taylor',
    type: 'User',
    initials: 'LT'
  }, {
    id: 2,
    name: 'John Smith',
    type: 'User',
    initials: 'JS'
  }, {
    id: 3,
    name: 'Sarah Johnson',
    type: 'User',
    initials: 'SJ'
  }, {
    id: 4,
    name: 'Michael Chen',
    type: 'User',
    initials: 'MC'
  }, {
    id: 5,
    name: 'Emily Davis',
    type: 'User',
    initials: 'ED'
  }, {
    id: 6,
    name: 'Alex Rivera',
    type: 'User',
    initials: 'AR'
  }, {
    id: 7,
    name: 'Lisa Wong',
    type: 'User',
    initials: 'LW'
  }, {
    id: 8,
    name: 'HTML Basics',
    type: 'Skill',
    initials: 'HB'
  }, {
    id: 9,
    name: 'CSS Fundamentals',
    type: 'Skill',
    initials: 'CF'
  }, {
    id: 10,
    name: 'JavaScript Basics',
    type: 'Skill',
    initials: 'JB'
  }, {
    id: 11,
    name: 'Responsive Design',
    type: 'Skill',
    initials: 'RD'
  }, {
    id: 12,
    name: 'Monthly Analytics Report',
    type: 'Report',
    initials: 'MA'
  }, {
    id: 13,
    name: 'Learner Progress Summary',
    type: 'Report',
    initials: 'LP'
  }, {
    id: 14,
    name: 'Skills Completion Report',
    type: 'Report',
    initials: 'SC'
  }, {
    id: 15,
    name: 'Assessment Performance',
    type: 'Report',
    initials: 'AP'
  }];
  const searchResults = searchQuery.length > 0 ? searchableItems.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()) || item.type.toLowerCase().includes(searchQuery.toLowerCase())) : [];
  const reportTemplates = [{
    id: 1,
    name: 'Learner Progress Report',
    description: 'Track individual learner progress across all skills',
    icon: Users,
    color: 'from-[#30718D] to-[#265a70]',
    fields: ['Learner Name', 'Skill', 'Date Range']
  }, {
    id: 2,
    name: 'Skills Analytics',
    description: 'Comprehensive analytics for specific skills',
    icon: BarChart3,
    color: 'from-[#007558] to-[#005a44]',
    fields: ['Skill', 'Date Range', 'Metrics']
  }, {
    id: 3,
    name: 'Assessment Results',
    description: 'Detailed assessment performance and scores',
    icon: FileBarChart,
    color: 'from-purple-500 to-purple-600',
    fields: ['Assessment', 'Date Range', 'Learner Group']
  }, {
    id: 4,
    name: 'Completion Summary',
    description: 'Overview of skill completions and certifications',
    icon: CheckCircle,
    color: 'from-[#007558] to-[#005a44]',
    fields: ['Date Range', 'Skill Category']
  }];
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-2551" data-mp="3uwi3ijh5ol4m">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2552" data-mp="3qv50eh5g6w1e">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2553" data-mp="8me78cst2ptn">
          <div className="flex items-center h-16" data-id="element-2554" data-mp="3om6cta6ntdb">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2555" data-mp="3mjhwdooi8ap">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2556" data-mp="375316qpdq3td">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2557" data-mp="37300ncqlosae">
                <Home size={18} data-id="element-2558" data-mp="3h1xwl0qcvo3f" />
                <span className="hidden sm:inline" data-id="element-2559" data-mp="2tdofgc437oim">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2560" data-mp="2tbbqvg3tqfwl">
                <BarChart3 size={18} data-id="element-2561" data-mp="13ug8nretcc81" />
                <span className="hidden sm:inline" data-id="element-2562" data-mp="18nsphfd93eq8">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-2563" data-mp="uw4ifqhuzgg4">
                <FileText size={18} data-id="element-2564" data-mp="1mmse4n0rjtme" />
                <span className="hidden sm:inline" data-id="element-2565" data-mp="1mntwlck3zie5">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2566" data-mp="1rlllrytdcqen">
                <UserCog size={18} data-id="element-2567" data-mp="1ik4aquutjewt" />
                <span className="hidden sm:inline" data-id="element-2568" data-mp="1in9ya5nsu8qf">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2569" data-mp="77bhffmdqien">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2570" data-mp="xw3r3iarzptj">
                <Bell size={20} data-id="element-2571" data-mp="ttgtt1vc5coy" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2572" data-mp="ovfacftzzbcy"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2573" data-mp="jx440f2wvizh">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2574" data-mp="jyx93pxvlv5d">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2575" data-mp="17u9phpxdidtn">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2576" data-mp="12tly3358fuyw">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2577" data-mp="1vj2o5u5dqzry" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2578" data-mp="22x3cl7hfb98n">
                    <div className="py-2" data-id="element-2579" data-mp="2ctpt5k2pve4v">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2580" data-mp="2csn4lisv9ns4">
                        <Users size={18} className="text-gray-600" data-id="element-2581" data-mp="2lturl4pxn26w" />
                        <span className="font-medium text-gray-900" data-id="element-2582" data-mp="2qs61ffg89cp4">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2583" data-mp="30ir7fny8dji2">
                        <Home size={18} className="text-gray-600" data-id="element-2584" data-mp="35jeujyjc9g70" />
                        <span className="font-medium text-gray-900" data-id="element-2585" data-mp="39j770mbus6hn">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2586" data-mp="l37atk5pv15y">
                        <UserCog size={18} className="text-gray-600" data-id="element-2587" data-mp="v2y63o23pk73" />
                        <span className="font-medium text-gray-900" data-id="element-2588" data-mp="zty4kxqvcabp">
                          Admin Hub
                        </span>
                      </button>
                      <div className="border-t border-gray-200 my-2" data-id="element-2589" data-mp="14srhop28tzbc"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2590" data-mp="18tl7xp35rq7e">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2591" data-mp="1drmreb4hxrje">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2592" data-mp="388whnhv3z55z"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2593" data-mp="33abh1hby2jh0"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2594" data-mp="2ybrtjchpczmj"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2595" data-mp="3vzidmaru8yzm">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-2596" data-mp="3h313tnzarsf6">
        {/* Header */}
        <div className="mb-8" data-id="element-2597" data-mp="2amfjybigqpjp">
          <div className="flex items-center gap-2 mb-2" data-id="element-2598" data-mp="2angxqnqv5q54">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-2599" data-mp="25mt928q3xle2">
              Reports
            </h1>
            <button type="button" data-phoebe-tour="report-builder" onClick={openReportBuilderPhoebe} aria-label="Ask Phoebe to help build a report" title="Build a report with Phoebe" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#30718D]/30 bg-white text-[#30718D] shadow-sm transition-colors hover:bg-[#30718D]/10 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-2600" data-mp="xoowubk76331">
              <Sparkles size={15} aria-hidden="true" data-id="element-2601" data-mp="iro1zz6pls0c" />
            </button>
          </div>
          <p className="text-gray-600 text-sm sm:text-base" data-id="element-2602" data-mp="50jajkdhw613">
            Generate, download, and manage your learning platform reports
          </p>
        </div>

        <ExecutiveValueReport data-id="element-2603" data-mp="3r5wmsl2zpehy" />

        {/* Report Templates */}
        <div data-phoebe-tour="reports" className="mb-8" data-id="element-2604" data-mp="3r8ix2sh9m83v">
          <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2605" data-mp="3w3y8ldrsvrx4">
            Generate New Report
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-2606" data-mp="3cjr8q9xlk7uc">
            {reportTemplates.map((template, index) => {
            const Icon = template.icon;
            return <motion.div key={template.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4 + index * 0.1
            }} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-2607" data-mp="3miylow0f60gx" data-mpi={index}>
                  <div className="flex items-start gap-4" data-id="element-2608" data-mp="j0otyb0ewk62">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${template.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`} data-id="element-2609" data-mp="j16n2yrs2i9r">
                      <Icon size={24} className="text-white" data-id="element-2610" data-mp="2cjnrizf7qcl6" />
                    </div>
                    <div className="flex-1" data-id="element-2611" data-mp="2chaynr7x2htc">
                      <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#30718D] transition-colors" data-id="element-2612" data-mp="2ha46wp2lg8a6">
                        {template.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-0" data-id="element-2613" data-mp="1om8dbt8bmwhm">
                        {template.description}
                      </p>
                      <div className="flex justify-end mt-4" data-id="element-2614" data-mp="1tlbe31h9qgjs">
                        <button className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white text-sm font-medium rounded-lg hover:bg-[#c02f1f] transition-colors" data-id="element-2615" data-mp="1yjmq9iv1d356">
                          <Download size={14} data-id="element-2616" data-mp="39z1kwfzrvix4" />
                          Download Report
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Download Reports */}
        <div data-phoebe-tour="download-reports" className="bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-2617" data-mp="yb671uhmksfk">
          <div className="p-4 sm:p-6" data-id="element-2618" data-mp="tk7oeo95zi95">
            <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2619" data-mp="tj4vybw717r4">
              Download Reports
            </h2>

            {/* Search and Download */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-id="element-2620" data-mp="jn1wxewn1p8e">
              <div className="relative flex-1" ref={searchContainerRef} data-id="element-2621" data-mp="fkoqeoa2hnak">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2622" data-mp="am3siviaffpa" />
                <input type="text" placeholder="Search for skills, learners or reports" value={selectedResult ? selectedResult.name : searchQuery} onChange={e => {
                if (selectedResult) {
                  setSelectedResult(null);
                }
                setSearchQuery(e.target.value);
              }} onFocus={() => setIsSearchFocused(true)} className={`w-full pl-9 ${selectedResult ? 'pr-9' : 'pr-3'} py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent text-sm ${selectedResult ? 'border-[#30718D]/30 bg-[#30718D]/5 text-[#30718D] font-medium' : 'border-gray-300'}`} data-id="element-2623" data-mp="5obxvtq9dfhe" />
                {selectedResult && <button onClick={() => {
                setSelectedResult(null);
                setSearchQuery('');
              }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2624" data-mp="lz45ecuu7dvb">
                    <X size={16} data-id="element-2625" data-mp="2wt9pi6spq70u" />
                  </button>}

                {/* Dropdown: Quick Options or Search Results */}
                {isSearchFocused && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-80 overflow-y-auto" data-id="element-2626" data-mp="1expp6tuq7qae">
                    {searchQuery.length === 0 ? <div data-id="element-2627" data-mp="1juznkz03fcad">
                        {quickOptionSections.map(section => <div key={section.label} data-id="element-2628" data-mp="w5euvoe510nw">
                            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-2629" data-mp="w66nqrgcokah">
                              <span className="text-xs font-medium text-black uppercase tracking-wide" data-id="element-2630" data-mp="r47o9snqe7it">
                                {section.label}
                              </span>
                            </div>
                            {section.items.map(option => <button key={option.id} onClick={() => {
                      setSelectedResult(selectedResult?.id === option.id ? null : option);
                      setIsSearchFocused(false);
                    }} className={`w-full flex items-center px-4 py-2.5 text-left text-sm transition-colors border-b border-gray-100 last:border-b-0 ${selectedResult?.id === option.id ? 'bg-[#30718D]/10 text-[#30718D] font-semibold' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-2631" data-mp="cfl8xqy3tfrl">
                                <span className="flex-1" data-id="element-2632" data-mp="1pzsoi2rr5q24">{option.name}</span>
                                {selectedResult?.id === option.id && <CheckCircle size={16} className="text-[#30718D]" data-id="element-2633" data-mp="1z07bd6kd7d7q" />}
                              </button>)}
                          </div>)}
                      </div> : searchResults.length > 0 ? searchResults.map(result => <button key={result.id} onClick={() => {
                  setSelectedResult(selectedResult?.id === result.id ? null : result);
                  setIsSearchFocused(false);
                }} className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${selectedResult?.id === result.id ? 'bg-[#30718D]/10' : 'hover:bg-gray-50'}`} data-id="element-2634" data-mp="2037kc268nac2">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${selectedResult?.id === result.id ? 'bg-[#30718D] text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-2635" data-mp="3fl9bhdilbz7j">
                            {result.initials}
                          </div>
                          <div className="flex-1" data-id="element-2636" data-mp="w7hpblzym3bm">
                            <div className="font-medium text-gray-900 text-sm" data-id="element-2637" data-mp="w89hsny9fkxo">
                              {result.name}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-2638" data-mp="1owye89odf233">
                              {result.type}
                            </div>
                          </div>
                          {selectedResult?.id === result.id && <CheckCircle size={18} className="text-[#30718D]" data-id="element-2639" data-mp="1f4snf39uuqph" />}
                        </button>) : <div className="px-4 py-6 text-center text-sm text-gray-500" data-id="element-2640" data-mp="27ohfaayu462h">
                        No results found for "{searchQuery}"
                      </div>}
                  </div>}
              </div>
              <button disabled={!selectedResult} className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedResult ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f] cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2641" data-mp="2f65uwgsuopuq">
                <Download size={16} data-id="element-2642" data-mp="2t5p0p2y6vp3v" />
                Download
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}